import { Link } from "react-router-dom";
import { Instagram, Twitter, Linkedin, Youtube, Mail, MapPin } from "lucide-react";
import { site, navLinks, socials } from "../../content/site";

const socialIcons = { instagram: Instagram, x: Twitter, linkedin: Linkedin, youtube: Youtube };

export default function Footer() {
  return (
    <footer className="bg-ink text-sand" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-8 pb-16 border-b border-sand/15">
          <div className="md:col-span-5">
            <img
              src="/brand/logo-footer.webp"
              alt="The PilCoo Foundation"
              className="h-10 w-auto mb-10"
              data-testid="footer-logo"
            />
            <p className="font-serif text-3xl md:text-4xl leading-tight tracking-tight max-w-sm">
              Small acts. Connected well.{" "}
              <em className="text-sand/70">Meaningful change.</em>
            </p>
            <p className="mt-6 text-sm text-sand/60 max-w-sm leading-relaxed">
              {site.name} — a compassion-led initiative built around three equal
              pillars: Animals, People and Nature.
            </p>
            <div className="mt-8 flex items-center gap-3">
              {socials.map((s) => {
                const Icon = socialIcons[s.key];
                const isExternal = s.href.startsWith("http");
                return (
                  <a
                    key={s.key}
                    href={s.href}
                    {...(isExternal ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                    aria-label={isExternal ? `${s.label} (opens in new tab)` : `${s.label} (coming soon)`}
                    data-testid={`footer-social-${s.key}`}
                    className="h-10 w-10 rounded-full border border-sand/25 flex items-center justify-center text-sand/70 transition-colors hover:text-sand hover:border-sand/60"
                  >
                    <Icon className="h-4 w-4" />
                  </a>
                );
              })}
            </div>
          </div>

          <div className="md:col-span-3">
            <h2 className="text-xs uppercase tracking-[0.25em] text-sand/50 mb-5">Explore</h2>
            <ul className="space-y-3">
              {navLinks.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="text-sm text-sand/75 hover:text-sand transition-colors"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/get-involved" data-testid="footer-link-get-involved" className="text-sm font-semibold text-sand hover:underline underline-offset-4">
                  Get Involved
                </Link>
              </li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <h2 className="text-xs uppercase tracking-[0.25em] text-sand/50 mb-5">Contact</h2>
            <a
              href={`mailto:${site.email}`}
              data-testid="footer-email"
              className="flex items-center gap-3 text-sm text-sand/75 hover:text-sand transition-colors"
            >
              <Mail className="h-4 w-4 shrink-0" /> {site.email}
            </a>
            <p className="mt-4 flex items-center gap-3 text-sm text-sand/75">
              <MapPin className="h-4 w-4 shrink-0" /> {site.city}
            </p>
            <p className="mt-8 text-xs leading-relaxed text-sand/45 max-w-xs">
              {site.disclaimer}
            </p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 py-8 text-xs text-sand/50">
          <p>© {new Date().getFullYear()} {site.name}. Animals · People · Nature.</p>
          <div className="flex gap-6">
            <Link to="/privacy" data-testid="footer-link-privacy" className="hover:text-sand transition-colors">Privacy Policy</Link>
            <Link to="/terms" data-testid="footer-link-terms" className="hover:text-sand transition-colors">Terms</Link>
            <Link to="/transparency" data-testid="footer-link-transparency" className="hover:text-sand transition-colors">Transparency</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
